export type OrganizationNodeType = 'company' | 'department' | 'member' | 'subdepartment'

export type PermissionState = 'checked' | 'indeterminate' | 'unchecked'

export interface OrganizationNode {
  children?: OrganizationNode[]
  directBinding?: boolean
  email?: string
  excluded?: boolean
  hasPermission?: boolean
  id: string
  jobTitle?: string
  memberCount?: number
  name: string
  parentId?: string
  type: OrganizationNodeType
}

export interface RawOrganizationNode {
  children?: RawOrganizationNode[]
  direct_binding?: boolean
  email?: string
  excluded?: boolean
  has_permission?: boolean
  id: string
  is_expanded?: boolean
  job_title?: string
  member_count?: number
  name: string
  type: OrganizationNodeType
}

export interface MemberIndexItem {
  deptIds: Set<string>
  member: OrganizationNode
}

export interface InitialPermissionState {
  excludedMemberIds: Set<string>
  memberIds: Set<string>
  orgIds: Set<string>
}

export interface MemberPermissionChanges {
  accountIdsToUnbind: string[]
  excludedToAdd: string[]
  hasChanges: boolean
  memberToAdd: string[]
  orgToAdd: string[]
  orgToRemove: string[]
}

export function normalizeOrganizationTree(
  node: RawOrganizationNode,
  parentId?: string,
): OrganizationNode {
  const children = node.children?.map((child) => normalizeOrganizationTree(child, node.id))

  return {
    children: children && children.length > 0 ? children : undefined,
    directBinding: Boolean(node.direct_binding),
    email: node.email,
    excluded: Boolean(node.excluded),
    hasPermission: Boolean(node.has_permission),
    id: node.id,
    jobTitle: node.job_title,
    memberCount: node.member_count,
    name: node.name,
    parentId,
    type: node.type,
  }
}

export function getOrganizationChildren(node: OrganizationNode | undefined) {
  return node?.children?.filter((child) => child.type !== 'member') ?? []
}

export function flattenMembers(root: OrganizationNode) {
  const map = new Map<string, MemberIndexItem>()

  function visit(node: OrganizationNode, deptInfo: OrganizationNode) {
    if (node.type === 'member') {
      const existing = map.get(node.id)

      if (existing) {
        existing.deptIds.add(deptInfo.id)
      } else {
        map.set(node.id, {
          deptIds: new Set([deptInfo.id]),
          member: node,
        })
      }
      return
    }

    node.children?.forEach((child) => visit(child, node))
  }

  visit(root, root)

  return map
}

export function flattenOrganizations(root: OrganizationNode) {
  const map = new Map<string, OrganizationNode>()

  function visit(node: OrganizationNode) {
    if (node.type !== 'member') {
      map.set(node.id, node)
    }

    node.children?.forEach(visit)
  }

  visit(root)

  return map
}

export function collectNodeMemberIds(root: OrganizationNode) {
  const map = new Map<string, string[]>()

  function visit(node: OrganizationNode): string[] {
    let ids: string[] = []

    if (node.type === 'member') {
      ids = [node.id]
    }

    node.children?.forEach((child) => {
      ids = ids.concat(visit(child))
    })

    if (node.type !== 'member') {
      map.set(node.id, ids)
    }

    return ids
  }

  visit(root)

  return map
}

export function collectExpandableOrgIds(root: OrganizationNode) {
  const ids = new Set<string>()

  function visit(node: OrganizationNode) {
    if (node.type !== 'member') {
      ids.add(node.id)
    }

    node.children?.forEach(visit)
  }

  visit(root)

  return ids
}

export function collectInitialPermissionState(root: OrganizationNode): InitialPermissionState {
  const memberIds = new Set<string>()
  const orgIds = new Set<string>()
  const excludedMemberIds = new Set<string>()

  function visit(node: OrganizationNode) {
    if (node.type === 'member') {
      if (node.directBinding && node.hasPermission) {
        memberIds.add(node.id)
      }

      if (node.excluded) {
        excludedMemberIds.add(node.id)
      }

      return
    }

    if (node.type !== 'company' && node.hasPermission) {
      orgIds.add(node.id)
    }

    node.children?.forEach(visit)
  }

  visit(root)

  return {
    excludedMemberIds,
    memberIds,
    orgIds,
  }
}

export function resolveOrgPathName(orgId: string, orgMap: Map<string, OrganizationNode>) {
  const names: string[] = []
  const visited = new Set<string>()
  let current = orgMap.get(orgId)

  while (current && !visited.has(current.id)) {
    visited.add(current.id)

    if (current.name) {
      names.push(current.name)
    }

    if (!current.parentId) {
      break
    }

    current = orgMap.get(current.parentId)
  }

  return names.reverse().join(' / ')
}

export function compactSelectedOrgIds(
  selectedOrgIds: ReadonlySet<string>,
  orgMap: Map<string, OrganizationNode>,
) {
  const result = new Set<string>()

  selectedOrgIds.forEach((orgId) => {
    let current = orgMap.get(orgId)
    let hasSelectedAncestor = false
    const visited = new Set<string>()

    while (current?.parentId && !visited.has(current.parentId)) {
      visited.add(current.parentId)

      if (selectedOrgIds.has(current.parentId)) {
        hasSelectedAncestor = true
        break
      }

      current = orgMap.get(current.parentId)
    }

    if (!hasSelectedAncestor) {
      result.add(orgId)
    }
  })

  return result
}

export function buildMemberPermissionChanges({
  excludedMemberIds,
  initialExcludedMemberIds,
  initialMemberIds,
  initialOrgIds,
  orgMap,
  selectedMemberIds,
  selectedOrgIds,
}: {
  excludedMemberIds: ReadonlySet<string>
  initialExcludedMemberIds: ReadonlySet<string>
  initialMemberIds: ReadonlySet<string>
  initialOrgIds: ReadonlySet<string>
  orgMap: Map<string, OrganizationNode>
  selectedMemberIds: ReadonlySet<string>
  selectedOrgIds: ReadonlySet<string>
}): MemberPermissionChanges {
  const memberToAdd = Array.from(selectedMemberIds).filter((id) => !initialMemberIds.has(id))
  const memberToRemove = Array.from(initialMemberIds).filter((id) => !selectedMemberIds.has(id))

  const excludedToAdd = Array.from(excludedMemberIds)
    .filter((id) => !initialExcludedMemberIds.has(id))
    .filter((id) => !selectedMemberIds.has(id))

  const excludedToRemove = Array.from(initialExcludedMemberIds)
    .filter((id) => !excludedMemberIds.has(id))
    .filter((id) => !selectedMemberIds.has(id))

  const excludedToRemoveEffective = excludedToRemove.filter((id) => !memberToAdd.includes(id))

  const compactOrgIds = compactSelectedOrgIds(selectedOrgIds, orgMap)
  const orgToAdd = Array.from(compactOrgIds).filter((id) => !initialOrgIds.has(id))
  const orgToRemove = Array.from(initialOrgIds).filter((id) => !selectedOrgIds.has(id))
  const accountIdsToUnbind = Array.from(new Set([...memberToRemove, ...excludedToRemoveEffective]))

  return {
    accountIdsToUnbind,
    excludedToAdd,
    hasChanges:
      memberToAdd.length > 0 ||
      accountIdsToUnbind.length > 0 ||
      excludedToAdd.length > 0 ||
      orgToAdd.length > 0 ||
      orgToRemove.length > 0,
    memberToAdd,
    orgToAdd,
    orgToRemove,
  }
}
